import { type ReactElement } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { OrganizationRole } from '@/lib/roles';
import { SecurityGroupDetail } from './SecurityGroupDetail';
import { SecurityGroupsList } from './SecurityGroupsList';

/**
 * Security Groups tab (KIR-3800).
 *
 * Flag-gated end to end: with `security_groups_policies` off the backend
 * disables the group APIs, so a direct deep-link redirects to the org root
 * rather than rendering a page whose every request would fail.
 */
export default function SecurityGroupsTab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();

  const { flags, isLoading: isFlagLoading } = useFeatureFlags([
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
  ]);
  const isEnabled =
    flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false;

  // AC #17: only owners and managers may create, edit or delete groups.
  const { hasAccess } = useRoleCheck({
    param: 'organizationId',
    anyOf: [OrganizationRole.Owner, OrganizationRole.Manager],
  });

  if (!organizationId) return null;
  if (isFlagLoading) return null;
  if (!isEnabled) return <Navigate to={`/${organizationId}`} replace />;

  return (
    <Routes>
      <Route
        index
        element={
          <SecurityGroupsList
            organizationId={organizationId}
            canEdit={hasAccess}
          />
        }
      />
      <Route
        path=":groupId"
        element={
          <SecurityGroupDetail
            organizationId={organizationId}
            canEdit={hasAccess}
          />
        }
      />
    </Routes>
  );
}
